/**
 * Leads Management Component
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Mail, Phone, MapPin, Calendar, Tag, ChevronDown, ChevronUp, Download } from "lucide-react";
import { authAxios } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL;

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

export default function LeadsManagement() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLead, setExpandedLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [leadStats, setLeadStats] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await authAxios.get(`/leads${params}`);
      setLeads(res.data);
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchLeadStats = useCallback(async () => {
    try {
      const res = await authAxios.get("/leads/stats/summary");
      setLeadStats(res.data);
    } catch (error) {
      // Stats are non-critical - log but don't show toast
      console.warn("Failed to load lead stats:", error.message);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchLeadStats();
  }, [fetchLeads, fetchLeadStats]);

  const downloadContactsCSV = async () => {
    setDownloading(true);
    try {
      const response = await authAxios.get("/leads/export/csv", {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `instaquote_contacts_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Contacts CSV downloaded!");
    } catch {
      toast.error("Failed to download contacts");
    } finally {
      setDownloading(false);
    }
  };

  const updateLeadStatus = useCallback(async (leadId, newStatus) => {
    try {
      await authAxios.patch(`/leads/${leadId}?status=${newStatus}`);
      toast.success("Lead status updated");
      fetchLeads();
      fetchLeadStats();
    } catch {
      toast.error("Failed to update lead status");
    }
  }, [fetchLeads, fetchLeadStats]);

  const addTag = useCallback(async (leadId, tag) => {
    try {
      await authAxios.post(`/leads/${leadId}/add-tag?tag=${encodeURIComponent(tag)}`);
      toast.success(`Tag "${tag}" added`);
      fetchLeads();
    } catch {
      toast.error("Failed to add tag");
    }
  }, [fetchLeads]);

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-blue-100 text-blue-800",
      quote_started: "bg-yellow-100 text-yellow-800",
      quote_completed: "bg-emerald-100 text-emerald-800",
      nurturing: "bg-purple-100 text-purple-800",
      converted: "bg-green-100 text-green-800",
      lost: "bg-red-100 text-red-800",
    };
    return <Badge className={styles[status] || styles.new}>{status.replace(/_/g, ' ')}</Badge>;
  };

  return (
    <div data-testid="leads-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          CRM / Leads
        </h1>
        <div className="flex items-center gap-3">
          <Button
            onClick={downloadContactsCSV}
            disabled={downloading}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="download-contacts-btn"
          >
            <Download className="w-4 h-4" />
            {downloading ? "Downloading..." : "Download All Contacts"}
          </Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leads</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="quote_started">Quote Started</SelectItem>
              <SelectItem value="quote_completed">Quote Completed</SelectItem>
              <SelectItem value="nurturing">Nurturing</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      {leadStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Leads</p>
              <p className="text-2xl font-bold text-slate-900">{leadStats.total_leads}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Quote Started</p>
              <p className="text-2xl font-bold text-yellow-600">{leadStats.by_status?.quote_started || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Quote Completed</p>
              <p className="text-2xl font-bold text-emerald-600">{leadStats.by_status?.quote_completed || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Nurturing</p>
              <p className="text-2xl font-bold text-orange-600">{leadStats.by_status?.nurturing || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Converted</p>
              <p className="text-2xl font-bold text-green-600">{leadStats.by_status?.converted || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : leads.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No leads found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {leads.map((lead) => (
            <Card key={lead.id} className="border-slate-200" data-testid={`lead-card-${lead.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-slate-900">{lead.name}</h3>
                      {getStatusBadge(lead.status)}
                    </div>
                    <div className="grid sm:grid-cols-4 gap-2 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {lead.email}
                      </span>
                      {lead.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {formatPhoneNumber(lead.phone)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                      </span>
                      {lead.address && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {lead.address}
                        </span>
                      )}
                    </div>
                    {lead.tags && lead.tags.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {lead.tags.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Select
                      value={lead.status}
                      onValueChange={(val) => updateLeadStatus(lead.id, val)}
                    >
                      <SelectTrigger className="w-40" data-testid={`lead-status-${lead.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="quote_started">Quote Started</SelectItem>
                        <SelectItem value="quote_completed">Quote Completed</SelectItem>
                        <SelectItem value="nurturing">Nurturing</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
                    >
                      {expandedLead === lead.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {expandedLead === lead.id && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Quick Tags</p>
                        <div className="flex flex-wrap gap-2">
                          {["Hot Lead", "Follow Up", "Budget Concern", "Seasonal", "Commercial"].map((tag) => (
                            <Button
                              key={tag}
                              variant="outline"
                              size="sm"
                              onClick={() => addTag(lead.id, tag)}
                              disabled={lead.tags?.includes(tag)}
                            >
                              <Tag className="w-3 h-3 mr-1" />
                              {tag}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Lead Info</p>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Created: {new Date(lead.created_at).toLocaleDateString()}
                          </p>
                          {lead.email_opt_in && <Badge variant="outline" className="text-xs">Email Opt-In</Badge>}
                          {lead.sms_opt_in && <Badge variant="outline" className="text-xs">SMS Opt-In</Badge>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
